/**
 * DeleteProgressModal - Terminal-style modal for project deletion progress
 *
 * Shows real-time progress as S3 files and database records are deleted.
 * Requires user to type "DELETE" to confirm the action.
 *
 * Uses SourceOptima corporate colors with red accent for destructive actions.
 */

import { useEffect, useState, useRef } from 'react'
import { FaCheck, FaExclamation, FaTimes, FaTrash } from "react-icons/fa";
import axios from 'axios';

const PHASES = [
  { name: 'Confirm', icon: '1' },
  { name: 'S3 Files', icon: '2' },
  { name: 'Database', icon: '3' },
  { name: 'Complete', icon: '4' },
]

// SourceOptima Corporate Colors
const COLORS = {
  deepTeal: '#1e5045',
  cream: '#f6f4ed',
  orange: '#f26522',
  white: '#fff',
  accentGreen: '#94c43d',
}

const LEVEL_COLORS = {
  phase: 'text-[#f26522] font-bold',      // Orange for phase headers
  success: 'text-[#94c43d]',               // Accent green for success
  info: 'text-gray-400',                   // Gray for info
  warning: 'text-[#f26522]',               // Orange for warnings
  error: 'text-red-400',                   // Red for errors
}

export default function DeleteProgressModal({
  folderName,
  spacePath,
  onComplete,
  onClose,
}) {
  const [confirmation, setConfirmation] = useState('')
  const [taskId, setTaskId] = useState(null)
  const [progress, setProgress] = useState(null)
  const [status, setStatus] = useState('WAITING') // WAITING, PENDING, PROGRESS, SUCCESS, FAILURE
  const [error, setError] = useState(null)
  const [startTime, setStartTime] = useState(null)
  const [logs, setLogs] = useState([
    { timestamp: 0, level: 'warning', message: 'WARNING: This action cannot be undone!' },
    { timestamp: 0.1, level: 'info', message: `Project: ${folderName}` },
    { timestamp: 0.2, level: 'info', message: 'Type DELETE to confirm deletion...' },
  ])
  const logContainerRef = useRef(null)
  const BASE_URL = process.env.REACT_APP_BE_MAIN_URL

  // Dynamic polling backoff state
  const [pollInterval, setPollInterval] = useState(500)
  const [concurrentUsers, setConcurrentUsers] = useState(0)
  const [isThrottled, setIsThrottled] = useState(false)

  // Auto-scroll log to bottom
  useEffect(() => {
    if (logContainerRef.current) {
      logContainerRef.current.scrollTop = logContainerRef.current.scrollHeight
    }
  }, [logs, progress?.status_log])

  // Start deletion process
  const handleStartDelete = async () => {
    if (confirmation !== 'DELETE') return

    const token = sessionStorage.getItem("userToken")
    setStartTime(Date.now())
    setStatus('PENDING')
    setError(null)

    setLogs(prev => [
      ...prev,
      { timestamp: 0, level: 'phase', message: '[START] Deletion confirmed. Starting process...' },
    ])

    try {
      const response = await axios.delete(
        `${BASE_URL}/ocr/delete-project/`,
        {
          headers: { Authorization: `Bearer ${token}` },
          data: {
            folder_name: folderName,
            space_path: spacePath,
            confirmation: 'DELETE'
          }
        }
      )

      setTaskId(response.data.task_id)
      setLogs(prev => [
        ...prev,
        { timestamp: 0.5, level: 'success', message: `Task started: ${response.data.task_id.substring(0, 8)}...` },
      ])
    } catch (err) {
      console.error('Failed to start delete task:', err)
      setStatus('FAILURE')
      setError(err.response?.data?.error || 'Failed to start deletion')
      setLogs(prev => [
        ...prev,
        { timestamp: 0.5, level: 'error', message: `[ERROR] ${err.response?.data?.error || 'Failed to start deletion'}` },
      ])
    }
  }

  // Poll for task status
  useEffect(() => {
    if (!taskId) return

    let isMounted = true
    let pollCount = 0
    const MAX_POLLS = 600 // 5 minutes at 500ms
    const token = sessionStorage.getItem("userToken")
    let currentPollInterval = 500 // Start with default, will be updated dynamically
    let timeoutId = null

    const poll = async () => {
      if (!isMounted || pollCount >= MAX_POLLS) return

      try {
        const response = await axios.get(
          `${BASE_URL}/ocr/status/${taskId}/`,
          { headers: { Authorization: `Bearer ${token}` } }
        )

        const data = response.data

        if (!isMounted) return

        // Update dynamic polling backoff state
        if (data.recommended_poll_interval_ms) {
          currentPollInterval = data.recommended_poll_interval_ms
          setPollInterval(data.recommended_poll_interval_ms)
        }
        if (data.concurrent_users !== undefined) {
          setConcurrentUsers(data.concurrent_users)
        }
        if (data.throttled !== undefined) {
          setIsThrottled(data.throttled)
        }

        setProgress(data.progress || {})
        setStatus(data.status)

        // Update logs from backend
        if (data.progress?.status_log) {
          setLogs(prev => {
            // Merge backend logs with local logs
            const backendLogs = data.progress.status_log.map(log => ({
              ...log,
              timestamp: log.timestamp || 0
            }))
            // Keep initial warning logs, add backend logs
            const initialLogs = prev.slice(0, 3)
            return [...initialLogs, ...backendLogs]
          })
        }

        if (data.status === 'SUCCESS') {
          setTimeout(() => {
            onComplete?.()
          }, 2000)
          return // Stop polling
        }

        if (data.status === 'FAILURE') {
          setError(data.error || 'Deletion failed')
          return // Stop polling
        }

        // Continue polling with dynamic interval
        pollCount++
        timeoutId = setTimeout(poll, currentPollInterval)

      } catch (err) {
        console.error('Poll error:', err)
        if (isMounted) {
          pollCount++
          timeoutId = setTimeout(poll, currentPollInterval)
        }
      }
    }

    poll()

    return () => {
      isMounted = false
      if (timeoutId) clearTimeout(timeoutId)
    }
  }, [taskId, BASE_URL, onComplete])

  // Calculate current phase
  const getCurrentPhase = () => {
    if (status === 'WAITING') return 0
    if (status === 'SUCCESS') return 4

    const message = progress?.status || ''
    if (message.toLowerCase().includes('database') || message.toLowerCase().includes('db')) return 2
    if (message.toLowerCase().includes('s3') || message.toLowerCase().includes('collecting')) return 1
    if (status === 'PENDING' || status === 'PROGRESS') return 1
    return 0
  }

  const currentPhase = getCurrentPhase()
  const elapsedSeconds = startTime ? ((Date.now() - startTime) / 1000).toFixed(1) : '0.0'
  const isDeleting = status === 'PENDING' || status === 'PROGRESS'

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-[#f6f4ed] rounded-xl shadow-2xl w-full max-w-2xl overflow-hidden border border-[#1e5045]/20">
        {/* Header - Red for destructive action */}
        <div className="bg-red-600 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="bg-white/20 p-2 rounded-lg">
              <FaTrash className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-white">Delete Project</h3>
              <p className="text-red-100 text-sm">{folderName}</p>
            </div>
          </div>
          {status === 'WAITING' && (
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors"
            >
              <FaTimes className="w-4 h-4 text-white" />
            </button>
          )}
        </div>

        <div className="p-6 space-y-6">
          {/* Phase Stepper */}
          <div className="flex flex-wrap gap-2">
            {PHASES.map((phase, idx) => {
              let bgColor = 'bg-[#1e5045]/10 text-[#1e5045]/50'
              let iconBg = 'bg-[#1e5045]/20'
              if (idx < currentPhase) {
                bgColor = 'bg-[#94c43d] text-white'
                iconBg = 'bg-white/30'
              } else if (idx === currentPhase && status !== 'WAITING') {
                bgColor = 'bg-red-500 text-white animate-pulse'
                iconBg = 'bg-white/20'
              }

              return (
                <div
                  key={phase.name}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all flex items-center gap-1 ${bgColor}`}
                >
                  <span className={`w-4 h-4 rounded-full ${iconBg} flex items-center justify-center text-[10px]`}>
                    {idx < currentPhase ? '✓' : phase.icon}
                  </span>
                  {phase.name}
                </div>
              )
            })}
          </div>

          {/* Confirmation Input (only when waiting) */}
          {status === 'WAITING' && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-sm text-red-800 mb-3">
                This will permanently delete all files from S3 and all records from the database.
                <br />
                <strong>This action cannot be undone.</strong>
              </p>
              <div className="flex gap-3">
                <input
                  type="text"
                  value={confirmation}
                  onChange={(e) => setConfirmation(e.target.value.toUpperCase())}
                  placeholder="Type DELETE to confirm"
                  className="flex-1 px-4 py-2 border border-red-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 font-mono bg-white"
                  autoFocus
                />
                <button
                  onClick={handleStartDelete}
                  disabled={confirmation !== 'DELETE'}
                  className={`px-6 py-2 rounded-lg font-medium transition-colors ${
                    confirmation === 'DELETE'
                      ? 'bg-red-600 text-white hover:bg-red-700'
                      : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  }`}
                >
                  Delete
                </button>
              </div>
            </div>
          )}

          {/* Progress Bar (when deleting) */}
          {(isDeleting || status === 'SUCCESS' || status === 'FAILURE') && (
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-[#1e5045]/70 truncate max-w-[70%]">
                  {progress?.status || 'Starting deletion...'}
                </span>
                <span className="font-medium text-red-600">
                  {elapsedSeconds}s
                </span>
              </div>
              <div className="h-2 bg-[#1e5045]/10 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all duration-300 ${
                    status === 'SUCCESS' ? 'bg-[#94c43d]' :
                    status === 'FAILURE' ? 'bg-red-500' :
                    'bg-gradient-to-r from-red-400 to-[#f26522]'
                  }`}
                  style={{ width: `${status === 'SUCCESS' ? 100 : (progress?.progress_pct || 5)}%` }}
                />
              </div>
              <div className="flex justify-between text-xs text-[#1e5045]/50">
                <span>
                  {progress?.current !== undefined && progress?.total !== undefined
                    ? `${progress.current} / ${progress.total}`
                    : ''}
                </span>
                <span>{Math.round(status === 'SUCCESS' ? 100 : (progress?.progress_pct || 0))}%</span>
              </div>
            </div>
          )}

          {/* Activity Log - Terminal Style */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-sm font-medium text-[#1e5045]">Activity Log</h4>
              <span className="text-xs text-[#1e5045]/40">
                {logs.length} entries
              </span>
            </div>
            <div
              ref={logContainerRef}
              className="bg-[#1e5045] rounded-lg p-3 h-48 overflow-y-auto font-mono text-xs"
            >
              {logs.map((entry, idx) => (
                <div
                  key={idx}
                  className={`py-0.5 ${LEVEL_COLORS[entry.level] || 'text-gray-400'} ${
                    entry.level === 'phase' ? 'mt-2 first:mt-0' : ''
                  }`}
                >
                  <span className="text-[#f6f4ed]/40 mr-2">
                    [{typeof entry.timestamp === 'number'
                      ? entry.timestamp.toFixed(1).padStart(6)
                      : '  0.0'}s]
                  </span>
                  {entry.message}
                </div>
              ))}
              {/* Throttle notification */}
              {isThrottled && isDeleting && (
                <div className="py-0.5 text-[#f26522] mt-2">
                  <span className="text-[#f6f4ed]/40 mr-2">[SYSTEM]</span>
                  High server activity ({concurrentUsers} users) - updates slowed to every {(pollInterval / 1000).toFixed(1)}s
                </div>
              )}
              {/* Blinking cursor when processing */}
              {isDeleting && (
                <div className="text-[#f26522] animate-pulse mt-1">▌</div>
              )}
            </div>
          </div>

          {/* Status Footer */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm">
              {status === 'SUCCESS' ? (
                <>
                  <FaCheck className="w-5 h-5 text-[#94c43d]" />
                  <span className="text-[#1e5045] font-medium">
                    Project deleted successfully! Redirecting...
                  </span>
                </>
              ) : status === 'FAILURE' ? (
                <>
                  <FaExclamation className="w-5 h-5 text-red-500" />
                  <span className="text-red-600 font-medium">{error || 'Deletion failed'}</span>
                </>
              ) : isDeleting ? (
                <>
                  <div className="w-5 h-5 border-2 border-red-500 border-t-transparent rounded-full animate-spin" />
                  <span className="text-[#1e5045]">Deleting project...</span>
                </>
              ) : null}
            </div>

            {/* Close button for failure state */}
            {status === 'FAILURE' && (
              <button
                onClick={onClose}
                className="px-4 py-2 bg-[#1e5045]/10 text-[#1e5045] rounded-lg hover:bg-[#1e5045]/20 transition-colors"
              >
                Close
              </button>
            )}

            {/* Cancel button when waiting */}
            {status === 'WAITING' && (
              <button
                onClick={onClose}
                className="px-4 py-2 bg-[#1e5045]/10 text-[#1e5045] rounded-lg hover:bg-[#1e5045]/20 transition-colors"
              >
                Cancel
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
